import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, CalendarCheck, ShieldCheck, Award, ArrowRight, Upload } from "lucide-react";

const RANGES = {
  "Elder Care Attendant": [400, 550], "Registered Nurse": [700, 1000], "Physiotherapist": [600, 900],
  "Counsellor": [900, 1500], "Yoga Coach": [400, 700], "Newborn Carer": [700, 1000],
};

export default function BecomeCarer() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [role, setRole] = useState("Elder Care Attendant");
  const [days, setDays] = useState(5);
  const [form, setForm] = useState({ name: "", email: "", password: "", city: "Hyderabad", phone: "" });
  const [loading, setLoading] = useState(false);

  const [lo, hi] = RANGES[role];
  const monthLow = lo * days * 4, monthHi = hi * days * 4;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Please fill name, email and password."); return; }
    setLoading(true);
    try {
      await register({ ...form, role: "carer" });
      toast.success("Application started! Welcome to BookMyCarer.");
      nav("/carer");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <section className="bg-ink text-ivory">
        <div className="bmc-wrap py-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-ivory">Build a career in care — on your terms.</h1>
          <p className="text-lg text-ivory/70 mt-4 max-w-2xl">Join 300+ verified carers earning ₹400–₹1,500/day in Hyderabad and Bengaluru.</p>
          <div className="flex flex-wrap gap-x-10 gap-y-4 mt-8">
            {[["300+", "Active carers"], ["₹400–₹1,500/day", "earnings"], ["< 48h", "Verification time"], ["100%", "On-time payouts"]].map(([n, l]) => (
              <div key={l}><p className="font-display font-bold text-2xl text-butter">{n}</p><p className="text-sm text-ivory/60">{l}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="bmc-wrap py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[[Wallet, "Earn well", "₹400–₹1,500/day based on your skill level. Set your own rates."], [CalendarCheck, "Your schedule", "Work as much or as little as you choose. Day, night or live-in."], [ShieldCheck, "Safety first", "Verified clients only. Female carers have an SOS panic button."], [Award, "Get certified", "Free BookMyCarer Certified credential after 10 bookings."]].map(([I, t, d]) => (
            <div key={t} className="card-bmc p-6"><I size={26} className="text-coral" /><h3 className="font-display font-bold mt-3">{t}</h3><p className="text-sm text-ink-soft mt-2">{d}</p></div>
          ))}
        </div>
      </section>

      <section className="bg-ivory-2 py-16">
        <div className="bmc-wrap grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow">Earnings calculator</div>
            <h2 className="text-3xl font-bold mt-2">See what you could earn</h2>
            <div className="card-bmc p-6 mt-6">
              <label className="text-sm font-semibold">Your role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full border border-line rounded-r px-3 py-2.5 mt-2 bg-ivory text-sm focus:outline-none" data-testid="calc-role">
                {Object.keys(RANGES).map((r) => <option key={r}>{r}</option>)}
              </select>
              <label className="text-sm font-semibold mt-4 block">Days per week: <span className="text-coral">{days}</span></label>
              <input type="range" min="1" max="7" value={days} onChange={(e) => setDays(+e.target.value)} className="w-full accent-coral mt-2" data-testid="calc-days" />
              <div className="bg-coral-soft rounded-r p-5 mt-5 text-center">
                <p className="text-sm text-coral-deep">Estimated monthly earnings</p>
                <p className="font-display font-bold text-3xl text-coral-deep mt-1" data-testid="calc-output">₹{monthLow.toLocaleString("en-IN")} – ₹{monthHi.toLocaleString("en-IN")}</p>
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold">How it works for carers</h2>
            <div className="space-y-5 mt-6">
              {[["1", "Register & verify", "Aadhaar e-KYC + credential upload, verified by AuthBridge in 24–48 hrs"], ["2", "Build your profile", "Set availability, rates, service areas and specialties"], ["3", "Start earning", "Receive bookings, complete engagements, get paid every Friday via UPI"]].map(([n, t, d]) => (
                <div key={n} className="flex gap-4"><span className="grid place-items-center w-9 h-9 rounded-full bg-sage text-white font-bold shrink-0">{n}</span><div><p className="font-semibold">{t}</p><p className="text-sm text-ink-soft mt-1">{d}</p></div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bmc-wrap py-16">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-center">Start your application</h2>
          <p className="text-ink-soft text-center mt-2">Joining is free. Platform fee is charged per completed booking only.</p>
          <form onSubmit={submit} className="card-bmc p-7 mt-8 space-y-4">
            <Field label="Full name"><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email"><input type="email" className="inp" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" /></Field>
              <Field label="Mobile (+91)"><input className="inp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Password"><input type="password" className="inp" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reg-password" /></Field>
              <Field label="City"><select className="inp" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="reg-city"><option>Hyderabad</option><option>Bengaluru</option></select></Field>
            </div>
            <Field label="Documents (Aadhaar, PAN, Qualification, Photo)">
              <div className="border-2 border-dashed border-line rounded-r p-6 text-center text-ink-soft text-sm"><Upload size={20} className="mx-auto mb-2" /> Upload after registration in your carer portal</div>
            </Field>
            <button disabled={loading} className="btn-pill bg-coral text-white w-full justify-center py-3 disabled:opacity-60" data-testid="reg-submit">{loading ? "Creating..." : "Start my application"} <ArrowRight size={16} /></button>
            <p className="text-xs text-ink-faint text-center">Payouts every Friday via UPI.</p>
          </form>
        </div>
      </section>
      <style>{`.inp{width:100%;border:1px solid var(--line);border-radius:12px;padding:10px 12px;background:var(--ivory);font-size:14px;outline:none}.inp:focus{border-color:var(--coral)}`}</style>
    </PublicLayout>
  );
}

function Field({ label, children }) {
  return <div><label className="text-sm font-semibold block mb-1.5">{label}</label>{children}</div>;
}
